import { groupBy } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useQuery } from "src/common/hooks";
import { Routes } from "src/common/Routes";
import { isNullOrUndefinedOrEmpty } from "../../../common/utils";
import {
  InformationAssetsAction,
  sendInformationAssetsEvent
} from "../../../components/matomoClient";
import { useFetchApisStatusForInformationAssetsPage } from "../hooks/api-progress/information-asset-page";
import { useFetchSystemUserGroupsApiCallState } from "../hooks/api-progress/system-user-group";
import { useFetchInformationAssetAccesses } from "../hooks/queries/information-asset-accesses";
import { useFetchInformationAssetUsers } from "../hooks/queries/information-asset-users";
import { useFetchAllResources } from "../hooks/queries/resources";
import { useFetchAllSystemResources } from "../hooks/queries/system-resources";
import { useFetchAllSystemUserGroups } from "../hooks/queries/system-user-groups";
import { AppliedScope } from "../models/current-applied-scopes";
import { InformationAssetUser, InformationAssetUserAction } from "../models/information-asset-user";
import { SystemResource } from "../models/system-resources";
import { SystemUserGroupMemberRecord } from "./model/view-object";

export const useSystemUserGroupDetailController = ({ assetId: resourceId, systemId, groupId }) => {
  let systemName: String = "";

  const dispatch = useDispatch();
  const history = useHistory();
  const query = useQuery();

  const fetchInformationSystemUserGroupsApiCallStatus = useFetchSystemUserGroupsApiCallState();
  const informationAssetsPageApisCallState = useFetchApisStatusForInformationAssetsPage();
  const [scopeToBeFiltered, setScopeToBeFiltered] = useState<string | null>(query.get("scope"));

  const isLoading =
    informationAssetsPageApisCallState.isApiCallLoading ||
    fetchInformationSystemUserGroupsApiCallStatus.isApiCallLoading;

  useEffect(() => {
        informationAssetsPageApisCallState.dispatchApiCall(false)
    }, [])

  const resources = useFetchAllResources();
  const systemUserGroups = useFetchAllSystemUserGroups();
  const systemResources = useFetchAllSystemResources();
  const currentSystemResourceAccesses = useFetchInformationAssetAccesses();

    const matchedResource = resources.getResources().find(resource => resource.id === resourceId);
    const matchedSystemUserGroup = systemUserGroups?.find((systemUserGroup) =>
      systemUserGroup.systemUserGroupId === groupId && systemUserGroup.systemId === systemId
  );
  const matchedSystemResources = systemResources.systemResources.filter(
    (systemResource) => systemResource.system.id === systemId
  );
  if (!isNullOrUndefinedOrEmpty(matchedSystemResources)) {
        systemName = matchedSystemResources[0].system.name
  }
    let members: string[] = []
    let groupName: String = ""
  if (matchedSystemUserGroup !== undefined) {
        members = matchedSystemUserGroup.members.map((member) => member.email)
        groupName = matchedSystemUserGroup.name
  }

  useEffect(() => {
    if (members.length !== 0) {
      dispatch(InformationAssetUserAction.getInformationAssetUsers(members));
    }
    return () => {
      dispatch(InformationAssetUserAction.resetInformationAssetSystemUserState());
        }
    }, [fetchInformationSystemUserGroupsApiCallStatus.isApiCallCompletedSuccessfully])

  const informationAssetUsers = [...useFetchInformationAssetUsers()];

  addUsersForWhomDetailsAreNotFetched();

  function addUsersForWhomDetailsAreNotFetched() {
    members
      .filter(
        (member) =>
          !informationAssetUsers.find(
            (informationAssetUser) => informationAssetUser.email === member
          )
      )
      .forEach((unverifiedMember) =>
        informationAssetUsers.push(
          new InformationAssetUser({
            email: unverifiedMember,
            employeeId: null,
            fullName: null,
            jobProfile: null,
          })
        )
      );
  }

    function buildAppliedScopes(user: InformationAssetUser):AppliedScope[] {
        const appliedScopesStringFormat: Set<string> = new Set<string>()
        currentSystemResourceAccesses.fetchSystemResourceAccessesForUser(user, systemUserGroups, systemId, resourceId)
      .forEach((systemResourceAccess) => {
        systemResourceAccess.resourceAccesses.forEach((access) =>
                    access.appliedScopesForUserGroupDetails(user.email)
                        .forEach((appliedScope) => appliedScopesStringFormat.add(appliedScope)));
            })
    const appliedScopes: AppliedScope[] = [];
        appliedScopesStringFormat.forEach((appliedScope)=>appliedScopes.push(new AppliedScope(JSON.parse(appliedScope))))
        return appliedScopes
  }

  function buildSystemResource(user: InformationAssetUser) {
        const matchedSystemResourceIds = currentSystemResourceAccesses.fetchSystemResourceAccessesForUser(user, systemUserGroups, systemId, resourceId).filter(
            (systemResourceAccess) =>
                systemResourceAccess.systemResource.systemId === systemId)
      ?.map((systemResourceAccess) => systemResourceAccess.systemResource?.systemResourceId);
        const foundSystemResources = matchedSystemResources.filter(
            (systemResource) => matchedSystemResourceIds.includes(systemResource.systemResourceId.toString()));
    return groupBy(foundSystemResources, (systemResource: SystemResource) => systemResource.type);
  }

  function allSystemUserGroupsOfUser(user: InformationAssetUser) {
    return systemUserGroups?.filter(
            systemUserGroup =>
                systemUserGroup.members.map(member => member.email).includes(user.email)
                && systemUserGroup.systemId === systemId
    );
  }

  function buildSystemUserGroupNames(user: InformationAssetUser) {
    const systemUserGroupsOfUser = allSystemUserGroupsOfUser(user);
        return systemUserGroupsOfUser.filter(
            systemUserGroup => systemUserGroup.systemUserGroupId !== groupId
        ).map(systemUserGroup => systemUserGroup.name.toString());
  }

  const systemUserGroupMemberRecords = informationAssetUsers.map((user) => {
    return new SystemUserGroupMemberRecord(
                user.email, user.jobProfile, user.fullName, user.employeeId,
                buildSystemUserGroupNames(user), buildAppliedScopes(user),buildSystemResource(user))
        }
    );

  function backButtonClickFunctionality() {
    history.push(`${Routes.InformationAssets.path}/${resourceId}/accessDetails`);
        sendInformationAssetsEvent(InformationAssetsAction.BackToAccessDetails)
  }

  const getResourceName = () => matchedResource?.name.toString();


  function scopesRelatedToSystemUserGroup() {
    return currentSystemResourceAccesses
      .overAllScopesForTheSystemUserGroup(systemId, groupId, resourceId)
      .map((currentAppliedScopeWithUserCount) => {
        return currentAppliedScopeWithUserCount.appliedScope.displayFormat();
      });
  }
  const filteredSystemUserGroupMemberRecords = scopeToBeFiltered
    ? systemUserGroupMemberRecords.filter((systemUserGroupMemberRecord) =>
        systemUserGroupMemberRecord.isAssociatedToScope(scopeToBeFiltered)
      )
    : systemUserGroupMemberRecords;

  function handleFilteredScopesNotRelatedToUserGroup() {
    if (
      !isLoading &&
      scopeToBeFiltered &&
      systemUserGroupMemberRecords.length > 0
    ) {
      if (
        !scopesRelatedToSystemUserGroup().some((scope) => {
          return scope === scopeToBeFiltered;
        })
      ) {
        setScopeToBeFiltered(null);
      }
    }
  }

  useEffect(handleFilteredScopesNotRelatedToUserGroup, [
    systemUserGroupMemberRecords,
    isLoading,
    scopeToBeFiltered,
  ]);

  return {
    filteredSystemUserGroupMemberRecords,
    groupName,
    backButtonClickFunctionality,
    systemName,
    isLoading,
    getResourceName,
    scopeToBeFiltered,
    scopesRelatedToSystemUserGroup,
    setScopeToBeFiltered,
  };
};
